import ApiController from '@/services/ApiController'
import { useDoiOptInStore } from '@/stores/doiOptIn'
import { useFeaturesStore } from '@/stores/features'
import { computed, unref, type MaybeRef } from 'vue'

const DOI_GATEKEEPER_FEATURE_NAME = 'double_opt_in'

/**
 * Shared double opt-in (DOI) checkbox logic (VIP-12044): gatekeeper check,
 * visibility, and the immediate-submit action used by the cart and
 * success-page touchpoints (checkout/OPC instead defer submission to the
 * order payload's doi_opt_in flag).
 *
 * The pending/24h-reshow decision itself is computed server-side
 * (`doi_show_checkbox` on the customer profile / success response) so it
 * isn't duplicated or clock-skewed across frontends — this composable only
 * combines that signal with the gatekeeper flag and this-purchase state.
 *
 * @param doiShowCheckbox - `doi_show_checkbox` from the customer profile (cart/checkout/OPC)
 *                          or successData (success page, covers guest orders too)
 */
export function useDoiOptIn(
  doiShowCheckbox: MaybeRef<boolean | null | undefined>
) {
  const featuresStore = useFeaturesStore()
  const doiOptInStore = useDoiOptInStore()

  const isDoiEnabled = computed(() =>
    featuresStore.isFeatureEnabled(DOI_GATEKEEPER_FEATURE_NAME)
  )

  const shouldShowCheckbox = computed(() => {
    if (!isDoiEnabled.value) return false
    if (doiOptInStore.hasOptedInThisPurchase) return false
    return !!unref(doiShowCheckbox)
  })

  async function submitDoiOptIn(
    email: string | null | undefined,
    source: 'cart' | 'success'
  ): Promise<void> {
    await ApiController.submitDoiOptIn({ email, source })
    doiOptInStore.markOptedIn()
  }

  return {
    isDoiEnabled,
    shouldShowCheckbox,
    submitDoiOptIn
  }
}
